import fp from 'lodash/fp';
import bytes from 'bytes';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { API } from '@factorin/container.macro';

import { callApi, apiCall } from 'Modules/core/api';
import { messagingActions, withErrorHandler } from 'Modules/core/messaging';
import { createUploadChannel, runChannel, augmentSaga, ActionPayload } from 'Modules/core/redux';
import { getDomainTypeFromPath } from 'Modules/business/data';

import { uploadsActions } from './actions';
import { Upload } from './types';


function* upload({ $payload }: ActionPayload<Upload>) {
    const { files, paginationPath, query } = $payload;

    const maxSizeMB = yield select(fp.get('environment.maxUploadFileSizeMB')) || 50;
    const uploadMaxSize = maxSizeMB * 1024 * 1024;

    const type = getDomainTypeFromPath(paginationPath);
    const size = fp.sumBy('size', files);
    if (fp.isEmpty(files)) {
        yield put(
            messagingActions.showNotification({
                content: 'uploads.emptyFiles',
            }),
        );
        return;
    }
    if (size > uploadMaxSize) {
        yield put(
            messagingActions.showNotification({
                content: {
                    key: 'uploads.maxSize',
                    values: {
                        size: bytes(size),
                        maxSize: bytes(uploadMaxSize),
                    },
                },
            }),
        );
        return;
    }
    yield all(files.map(file => {
        const data = new window.FormData();
        data.append('FileToUpload', file);
        return apiCall(API.domain[type].upload, { data, query });
    }));
    yield put(
        messagingActions.showNotification({
            content: {
                key: 'uploads.success',
                values: {
                    count: files.length,
                },
            },
        }),
    );
}

export function* uploadFileWithProgress({
    id,
    query,
    data,
    type,
    formDataKey,
    additionalInfo = {},
}) {
    const formData = new window.FormData();
    formData.append(formDataKey, data);
    Object.keys(additionalInfo).forEach(k => {
        formData.append(k, additionalInfo[k]);
    });

    const progressChannel = createUploadChannel(
        args => callApi(API.domain[type].upload, args),
        { data: formData, query },
    );
    return yield runChannel(
        progressChannel,
        progress => uploadsActions.progressUpload({ id, ...progress }),
        () => uploadsActions.finishUpload(id),
    );
}

export const sagas = [
    takeEvery(`${uploadsActions.upload}`, augmentSaga(
        upload,
        withErrorHandler('uploads.uploadError'),
    )),
];
